import { GetStaticProps } from 'next';
import Image from 'next/image';
import showdown from 'showdown';
import React from 'react';
import { useRef } from 'react';

import { Hero } from '../../components/Hero';
import { Container } from '../../components/Container';
import { ShareContainer } from '../../components/ShareContainer';

import { ApiNewAndEventsPageNewAndEventsPage } from '../../types/strapi/contentTypes';
import { IHead } from '../../types';
import { IImageResponse } from '../../types';

import { addSiteUrlToPath } from '../../helpers';
import { getCookieConsent } from '../../helpers/strapi-client';
import { getLanguageSpecificRoute } from '../../helpers/routing';
import { getNews } from '../../helpers/strapi-client';
import { getNewsBySlug } from '../../helpers/strapi-client';
import { getPageData } from '../../helpers/strapi-client';
import { getStaticUrl } from '../../helpers';
import { removeParagraph } from '../../helpers/showdown';
import { setAnimationConfig } from '../../hooks/useAnimation';
import { useAnimation } from '../../hooks';
import { useWindowDimensions } from '../../hooks';

interface INewsItem {
  title: string;
  content: string;
  summary: string;
  thumbnail?: IImageResponse;
  head: IHead;
}

export default function NewsItem({
  head,
  thumbnail = {} as IImageResponse,
  title = '',
  summary = '',
  content = '',
}: INewsItem) {
  const classMap: { [key: string]: string } = {
    p: 'mb-10',
    a: 'text-[#DA0000]',
  };

  const bindings = Object.keys(classMap).map((key) => ({
    type: 'output',
    regex: new RegExp(`<${key}(.*)>`, 'g'),
    replace: `<${key} class="${classMap[key]}" $1>`,
  }));

  const contentConverter = new showdown.Converter({
    extensions: [...bindings],
  });

  const summaryConverter = new showdown.Converter({
    extensions: [removeParagraph],
  });

  const parsedContent = contentConverter.makeHtml(content);
  const parsedSummary = summaryConverter.makeHtml(summary);
  const containerRef = useRef(null);

  useAnimation({
    containerRef,
  });

  const {
    breakPoints: { 'lg-': isMobile },
  } = useWindowDimensions();

  const heroMedia = thumbnail!.data?.attributes ? (
    <Image
      src={getStaticUrl(thumbnail.data.attributes.url)}
      layout="fill"
      objectFit="cover"
      objectPosition={isMobile ? 'center top' : 'bottom right'}
      alt={title}
    />
  ) : (
    <></>
  );

  return (
    <>
      <Container className="px-0">
        <Hero
          className="mb-16"
          media={heroMedia}
          title={title}
          extraInfo={
            <ShareContainer className="mt-4" showTitle title={head.title} />
          }
          shouldShowShare
          isInContainer
        />
      </Container>
      <Container
        className="h-full flex flex-1 relative mb-20 z-10"
        ref={containerRef}
      >
        <div className="flex flex-col">
          <h3
            className="text-[2rem] leading-[2.375rem] font-light md:w-2/3 mb-[3.75rem] invisible"
            data-animation={setAnimationConfig({
              type: 'slide-in-fade',
              delay: 0,
              duration: 4,
              from: 'left',
            })}
            dangerouslySetInnerHTML={{ __html: parsedSummary }}
          />
          <div
            className="md:px-20 lg:px-60 text-xl leading-[1.875rem] font-light invisible"
            data-animation={setAnimationConfig({
              type: 'slide-in-fade',
              delay: 4,
              duration: 4,
              from: 'left',
            })}
          >
            <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
            <ShareContainer className="mt-16" title={head.title} />
          </div>
        </div>
      </Container>
    </>
  );
}

export async function getStaticPaths() {
  const newsEntities = await getNews(100, 1); // TODO: handle pagination on Strapi

  const paths = newsEntities.map((item) => {
    return {
      params: {
        news: getLanguageSpecificRoute('news'),
        slug: item.attributes.slug,
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
}

export const getStaticProps: GetStaticProps<
  INewsItem,
  { slug: string; news: string }
> = async ({ params }) => {
  const cookieConsentData = await getCookieConsent();
  const news = await getNewsBySlug(params!.slug);
  const pageData = await getPageData<ApiNewAndEventsPageNewAndEventsPage>(
    'new-and-events-page',
    [
      'footer.map_image',
      'footer.company_entities',
      'footer.legal_documents.document',
      'routes',
      'hero',
      'hero.thumbnail',
      'image',
      'social_contents',
      'social_contents.images',
    ]
  );

  const head = {
    title: pageData?.attributes.hero.data.attributes.title,
    description: pageData?.attributes.hero?.data.attributes.subtitle,
    image: {
      url: addSiteUrlToPath(
        getStaticUrl(
          pageData?.attributes.hero?.data.attributes.thumbnail?.data.attributes
            .url
        )
      ),
      width:
        pageData?.attributes.hero?.data.attributes.thumbnail?.data.attributes
          .width,
      height:
        pageData?.attributes.hero?.data.attributes.thumbnail?.data.attributes
          .height,
    },
  };

  const footer = {
    company_details: pageData.attributes.footer,
    facebook_url: pageData.attributes.footer.facebook_url,
    twitter_url: pageData.attributes.footer.twitter_url,
    instagram_url: pageData.attributes.footer.instagram_url,
    linkedin_url: pageData.attributes.footer.linkedin_url,
    company_name: pageData.attributes.footer.company_name,
    companies_title: pageData.attributes.footer.companies_title,
    legal_documents_title: pageData.attributes.footer.legal_documents_title,
    company_entities: pageData.attributes.footer.company_entities,
    map_image: pageData.attributes.footer.map_image.data,
    legal_documents: pageData.attributes.footer.legal_documents.data,
  };

  return {
    props: {
      cookieConsentData,
      head,
      title: news.attributes.title,
      thumbnail: news.attributes.thumbnail,
      summary: news.attributes.summary,
      content: news.attributes.content,
      routes: pageData.attributes.routes.data,
      footer,
    },
  };
};
